import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Navbar from '@/components/Navbar'
import HeaderBar from '@/components/HeaderBar'
import DateInput from '@/components/DateInput'
import './AuditLogsPage.css'
import { Helmet } from 'react-helmet-async'
import { PAGES } from '@/constants/pages'
import { authService, PermissionScope } from '@/services/AuthService'
import { RESOURCES } from '@/constants/resources'
import { exportExcel } from '@/utils/spreadsheetHelpers'
import { AUDIT_LOGS_TEMPLATE, AUDIT_LOGS_WITH_FULL_ACCESS_TEMPLATE } from '@/constants/templateSchema'
import { PartnerSimProviderService } from '@/services/PartnerSimProviderService'
import { PartnerConfigurationService } from '@/services/PartnerConfigurationService'
import { AuditLogService } from '@/services/AuditLogService'
import { AuditLog } from '@/services/PartnerAuditLogService'

export default function AuditLogsPage() {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [itemTypeName, setItemTypeName] = useState<string>('')
  const [filterAction, setFilterAction] = useState<string>('')
  const [filterChangedBy, setFilterChangedBy] = useState<string>('')
  const [filterStartDate, setFilterStartDate] = useState<string>('')
  const [filterEndDate, setFilterEndDate] = useState<string>('')

  // Lookup maps for resolving IDs to names
  const [simProviders, setSimProviders] = useState<Map<string, string>>(new Map())
  const [configurations, setConfigurations] = useState<Map<string, string>>(new Map())

  const navigate = useNavigate()

  const hasFullAccess = authService.hasPermission([RESOURCES.PLATFORM_AUDIT_LOGS], PermissionScope.CREATE);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        const result = await AuditLogService.getAll()

        // Build lookup maps
        const simProvidersMap = new Map<string, string>()
        result.lookups.simProviders.forEach(sp => simProvidersMap.set(sp.id, sp.name))
        setSimProviders(simProvidersMap)

        const configurationsMap = new Map<string, string>()
        result.lookups.configurations.forEach(c => configurationsMap.set(c.id, c.name))
        setConfigurations(configurationsMap)

        setAuditLogs(result.logs)
      } catch (err) {
        console.error('Failed to fetch audit logs:', err)
        setError('Failed to load audit logs')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [navigate])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString()
  }

  const parseJsonSafely = (jsonString: string | null | undefined): Record<string, unknown> | null => {
    if (!jsonString) return null
    try {
      return JSON.parse(jsonString)
    } catch {
      return null
    }
  }

  const getChangedFieldsCount = (log: AuditLog): number => {
    const changedProps = parseJsonSafely(log.changedProperties) as string[] | null
    return changedProps?.length || 0
  }

  const getChangedFields = (log: AuditLog): string[] => {
    const changedProps = parseJsonSafely(log.changedProperties) as string[] | null
    return changedProps || []
  }

  const formatValue = (value: unknown, fieldName?: string): string => {
    if (value === null || value === undefined) return 'N/A'
    if (typeof value === 'boolean') return value ? 'true' : 'false'
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2)
    }

    // 1. Allow both numbers and strings for the lookup logic
    if (fieldName && (typeof value === 'number' || typeof value === 'string')) {
      const lowerFieldName = fieldName.toLowerCase()

      // 2. Convert value to a string so it matches the Map's key type
      const stringId = String(value)

      if (lowerFieldName.includes('simprovider')) {
        return simProviders.get(stringId) || `Unknown SIM Provider (ID: ${value})`
      }

      if (lowerFieldName.includes('configuration')) {
        return configurations.get(stringId) || `Unknown Configuration (ID: ${value})`
      }
    }

    return String(value)
  }

  const getOldValue = (log: AuditLog, field: string): string => {
    const oldValues = parseJsonSafely(log.oldValues) as Record<string, unknown> | null
    if (!oldValues || !(field in oldValues)) return 'N/A'
    return formatValue(oldValues[field], field)
  }

  const getNewValue = (log: AuditLog, field: string): string => {
    const newValues = parseJsonSafely(log.newValues) as Record<string, unknown> | null
    if (!newValues || !(field in newValues)) return 'N/A'
    return formatValue(newValues[field], field)
  }

  // Deep comparison to find changed properties in nested JSON objects
  const getChangedPropertiesFromJson = (oldObj: unknown, newObj: unknown, prefix = ''): Array<{path: string, oldValue: unknown, newValue: unknown}> => {
    const changes: Array<{path: string, oldValue: unknown, newValue: unknown}> = []

    if (typeof oldObj !== 'object' || typeof newObj !== 'object' || oldObj === null || newObj === null) {
      return []
    }

    const oldRecord = oldObj as Record<string, unknown>
    const newRecord = newObj as Record<string, unknown>
    const allKeys = new Set([...Object.keys(oldRecord), ...Object.keys(newRecord)])

    for (const key of allKeys) {
      const fullPath = prefix ? `${prefix}.${key}` : key
      const oldVal = oldRecord[key]
      const newVal = newRecord[key]

      // Check if both are objects and need deep comparison
      if (typeof oldVal === 'object' && typeof newVal === 'object' && oldVal !== null && newVal !== null && !Array.isArray(oldVal) && !Array.isArray(newVal)) {
        const nestedChanges = getChangedPropertiesFromJson(oldVal, newVal, fullPath)
        changes.push(...nestedChanges)
      } else if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({ path: fullPath, oldValue: oldVal, newValue: newVal })
      }
    }

    return changes
  }

  const parseFieldPath = (field: string): { section: string; subsection: string } => {
    // Parse field paths like "NoIncap.Enabled" or simple fields like "Name"
    const parts = field.split('.')
    if (parts.length === 1) {
      return { section: field, subsection: 'N/A' }
    } else if (parts.length === 2) {
      return { section: parts[0], subsection: parts[1] }
    } else {
      return { section: parts[0], subsection: parts.slice(1).join('.') }
    }
  }

  const exportToExcel = async () => {
    const csvRows: string[][] = []
  
    filteredLogs.forEach((log) => {
      const changedFields = getChangedFields(log)
      const oldValues = parseJsonSafely(log.oldValues) as Record<string, unknown> | null
      const newValues = parseJsonSafely(log.newValues) as Record<string, unknown> | null
  
      if (!changedFields || changedFields.length === 0) {
        if (log.action === 'Created' && newValues) {
          const entityName = newValues.Name || newValues.name || newValues.DeviceUuid || 'Entity'
          const row = hasFullAccess
            ? [
                log.channelPartnerName || 'Unknown',
                log.entityName,
                'N/A',
                'Created',
                'N/A',
                String(entityName),
                formatDate(log.changedAtUtc),
                formatTime(log.changedAtUtc),
                log.changedBy || 'System'
              ]
            : [
                log.entityName,
                'Created',
                'N/A',
                'N/A',
                String(entityName),
                formatDate(log.changedAtUtc),
                formatTime(log.changedAtUtc),
                log.changedBy || 'System'
              ]
          csvRows.push(row)
          return
        } 

        const row = hasFullAccess
          ? [
              log.channelPartnerName || 'Unknown',
              log.entityName,
              'N/A',
              log.action,
              'N/A',
              'N/A',
              formatDate(log.changedAtUtc),
              formatTime(log.changedAtUtc),
              log.changedBy || 'System'
            ]
          : [
              log.entityName,
              'N/A',
              log.action,
              'N/A',
              'N/A',
              formatDate(log.changedAtUtc),
              formatTime(log.changedAtUtc),
              log.changedBy || 'System'
            ]
        csvRows.push(row)
        return
      }

      const allChanges: Array<{field: string, section: string, subsection: string, oldValue: string, newValue: string, action: string}> = []

      changedFields.forEach(field => {
        const oldVal = oldValues?.[field]
        const newVal = newValues?.[field]

        if (typeof oldVal === 'object' && typeof newVal === 'object' && oldVal !== null && newVal !== null) {
          const nestedChanges = getChangedPropertiesFromJson(oldVal, newVal, field)
          nestedChanges.forEach(change => {
            const { section, subsection } = parseFieldPath(change.path)
            allChanges.push({
              field: change.path,
              section,
              subsection,
              oldValue: formatValue(change.oldValue, change.path),
              newValue: formatValue(change.newValue, change.path),
              action: log.action
            })
          })
        } else {
          const { section, subsection } = parseFieldPath(field)
          allChanges.push({
            field,
            section,
            subsection,
            oldValue: getOldValue(log, field),
            newValue: getNewValue(log, field),
            action: log.action
          })
        }
      })

      allChanges.forEach(change => {
        const row = hasFullAccess
          ? [
              log.channelPartnerName || 'Unknown',
              log.entityName,
              change.field,
              change.action,
              change.oldValue,
              change.newValue,
              formatDate(log.changedAtUtc),
              formatTime(log.changedAtUtc),
              log.changedBy || 'System'
            ]
          : [
              log.entityName,
              change.field,
              change.action,
              change.oldValue,
              change.newValue,
              formatDate(log.changedAtUtc),
              formatTime(log.changedAtUtc),
              log.changedBy || 'System'
            ]
        csvRows.push(row)
    })
  })
    
    if (hasFullAccess) {
      exportExcel(AUDIT_LOGS_WITH_FULL_ACCESS_TEMPLATE.name, AUDIT_LOGS_WITH_FULL_ACCESS_TEMPLATE.columns, csvRows);
    } else {
      exportExcel(AUDIT_LOGS_TEMPLATE.name, AUDIT_LOGS_TEMPLATE.columns, csvRows);
    }
  }
  
  const filteredLogs = auditLogs.filter(log => {
    if (itemTypeName && !log.entityName.toLowerCase().includes(itemTypeName.toLowerCase())) {
      return false
    }
    if (filterAction && !log.action.toLowerCase().includes(filterAction.toLowerCase())) {
      return false
    }
    if (filterChangedBy && log.changedBy && !log.changedBy.toLowerCase().includes(filterChangedBy.toLowerCase())) {
      return false
    }

    // Date range filtering
    const logDate = new Date(log.changedAtUtc)
    if (filterStartDate) {
      const startDate = new Date(filterStartDate)
      startDate.setHours(0, 0, 0, 0)
      if (logDate < startDate) {
        return false
      }
    }
    if (filterEndDate) {
      const endDate = new Date(filterEndDate)
      endDate.setHours(23, 59, 59, 999)
      if (logDate > endDate) {
        return false
      }
    }

    return true
  })

  if (loading) {
    return (
      <div className="page-container">
        <Navbar />
        <main className="page-main-content">
          <HeaderBar />
          <div className="page-content">
            <h2>System Audit Logs</h2>
            <div className="loading">Loading audit logs...</div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="page-container">
      <Helmet>
        <title>{PAGES.PLATFORM_AUDIT_LOGS.title}</title>
      </Helmet>
      <Navbar />
      <main className="page-main-content">
        <HeaderBar />
        <div className="page-content">
          <h2>{hasFullAccess ? 'System Audit Logs' : 'Audit Logs'}</h2>

          {error && (
            <div className="error-message">
              Failed to load audit logs
            </div>
          )}

          {!error && (
            <>
              <div className="audit-filters">
                <div className="filter-group">
                  <label htmlFor="item-filter">Item Type:</label>
                  <input
                    id="item-filter"
                    type="text"
                    placeholder="Filter by item type (e.g., Device)"
                    value={itemTypeName}
                    onChange={(e) => setItemTypeName(e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label htmlFor="action-filter">Action:</label>
                  <input
                    id="action-filter"
                    type="text"
                    placeholder="Filter by action (e.g., Updated)"
                    value={filterAction}
                    onChange={(e) => setFilterAction(e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label htmlFor="user-filter">Changed By:</label>
                  <input
                    id="user-filter"
                    type="text"
                    placeholder="Filter by user"
                    value={filterChangedBy}
                    onChange={(e) => setFilterChangedBy(e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label htmlFor="start-date-filter">Start Date:</label>
                  <DateInput
                    id="start-date-filter"
                    value={filterStartDate}
                    onChange={setFilterStartDate}
                    className="filter-input"
                  />
                </div>
                <div className="filter-group">
                  <label htmlFor="end-date-filter">End Date:</label>
                  <DateInput
                    id="end-date-filter"
                    value={filterEndDate}
                    onChange={setFilterEndDate}
                    className="filter-input"
                  />
                </div>
                <button
                  className="clear-filters-btn"
                  onClick={() => {
                    setItemTypeName('')
                    setFilterAction('')
                    setFilterChangedBy('')
                    setFilterStartDate('')
                    setFilterEndDate('')
                  }}
                >
                  Clear Filters
                </button>
              </div>

              <div className="audit-logs-stats">
                <div className="stats-left">
                  <span>Total Logs: {auditLogs.length}</span>
                  <span>Filtered Results: {filteredLogs.length}</span>
                </div>
                <button
                  className="export-btn"
                  onClick={exportToExcel}
                  disabled={filteredLogs.length === 0}
                >
                  Export to CSV
                </button>
              </div>

              {filteredLogs.length === 0 ? (
                <div className="no-logs">
                  {auditLogs.length === 0
                    ? 'No audit logs found.'
                    : 'No audit logs match the current filters.'}
                </div>
              ) : (
                <div className="audit-table-container">
                  <table className="audit-table">
                    <thead>
                      <tr>
                        {hasFullAccess && <th>Partner</th>}
                        <th>Item Type</th>
                        <th>Sub Item Type</th>
                        <th>Action</th>
                        <th>Previous Data</th>
                        <th>Data After Change</th>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Changed By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.map((log) => {
                        const changedFields = getChangedFields(log)
                        const oldValues = parseJsonSafely(log.oldValues) as Record<string, unknown> | null
                        const newValues = parseJsonSafely(log.newValues) as Record<string, unknown> | null

                        // For Created/Deleted actions or when no specific fields changed
                        if (changedFields.length === 0) {
                          // For Created action, extract the name field to display
                          if (log.action === 'Created' && newValues) {
                            const entityName = newValues.Name || newValues.name || newValues.Email || newValues.DeviceUuid || newValues.DeviceSerialNumber || 'Entity'

                            return (
                              <tr key={`${log.id}`}>
                                {hasFullAccess && <td>{log.channelPartnerName || 'Unknown'}</td>}
                                <td>{log.entityName}</td>
                                <td>N/A</td>
                                <td>Created</td>
                                <td>N/A</td>
                                <td className="value-cell" title={String(entityName)}>{String(entityName)}</td>
                                <td className="date-cell">{formatDate(log.changedAtUtc)}</td>
                                <td className="time-cell">{formatTime(log.changedAtUtc)}</td>
                                <td className="user-cell">{log.changedBy || 'System'}</td>
                              </tr>
                            )
                          }

                          // For Deleted or other actions without specific field changes
                          return (
                            <tr key={`${log.id}`}>
                              {hasFullAccess && <td>{log.channelPartnerName || 'Unknown'}</td>}
                              <td>{log.entityName}</td>
                              <td>N/A</td>
                              <td>{log.action}</td>
                              <td>N/A</td>
                              <td>N/A</td>
                              <td className="date-cell">{formatDate(log.changedAtUtc)}</td>
                              <td className="time-cell">{formatTime(log.changedAtUtc)}</td>
                              <td className="user-cell">{log.changedBy || 'System'}</td>
                            </tr>
                          )
                        }

                        // Collect all changes (including nested JSON changes)
                        const allChanges: Array<{field: string, section: string, subsection: string, oldValue: string, newValue: string, action: string}> = []

                        changedFields.forEach(field => {
                          const oldVal = oldValues?.[field]
                          const newVal = newValues?.[field]

                          // Check if both values are objects (JSON) - if so, do deep comparison
                          if (typeof oldVal === 'object' && typeof newVal === 'object' && oldVal !== null && newVal !== null) {
                            const nestedChanges = getChangedPropertiesFromJson(oldVal, newVal, field)
                            nestedChanges.forEach(change => {
                              const { section, subsection } = parseFieldPath(change.path)
                              allChanges.push({
                                field: change.path,
                                section,
                                subsection,
                                oldValue: formatValue(change.oldValue),
                                newValue: formatValue(change.newValue),
                                action: log.action
                              })
                            })
                          } else {
                            // Simple value change
                            const { section, subsection } = parseFieldPath(field)
                            allChanges.push({
                              field,
                              section,
                              subsection,
                              oldValue: getOldValue(log, field),
                              newValue: getNewValue(log, field),
                              action: log.action
                            })
                          }
                        })

                        return allChanges.map((change, index) => (
                          <tr key={`${log.id}-${change.field}-${index}`}>
                            {hasFullAccess && <td>{log.channelPartnerName || 'Unknown'}</td>}
                            <td>{log.entityName}</td>
                            <td>{change.field}</td>
                            <td>{change.action}</td>
                            <td className="value-cell" title={change.oldValue}>{change.oldValue}</td>
                            <td className="value-cell" title={change.newValue}>{change.newValue}</td>
                            <td className="date-cell">{formatDate(log.changedAtUtc)}</td>
                            <td className="time-cell">{formatTime(log.changedAtUtc)}</td>
                            <td className="user-cell">{log.changedBy || 'System'}</td>
                          </tr>
                        ))
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  )
}
